import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ShoppingCart, Heart, Star, ArrowLeft, Share2, Truck, Shield, RotateCcw } from 'lucide-react';
import './ProductDetails.css';
import usePageTitle from '../hooks/usePageTitle';
import storeVinyl from '../assets/images/store-vinyl.svg';
import storeShirt from '../assets/images/store-shirt.svg';
import storeGuitarPick from '../assets/images/store-guitar-pick.svg';
import storePoster from '../assets/images/store-poster.svg';
import storeCap from '../assets/images/store-cap.svg';

const ProductDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [selectedQuantity, setSelectedQuantity] = useState(1);
  const [selectedSize, setSelectedSize] = useState<string>('');
  const [isWishlisted, setIsWishlisted] = useState(false);

  // Mock product data - in real app this would come from API
  const products = [
    {
      id: 1,
      name: 'Elephant Vinyl LP',
      category: 'vinyl',
      price: 29.99,
      originalPrice: 34.99,
      image: storeVinyl,
      description: 'Limited edition red vinyl pressing of the iconic album',
      longDescription: 'This limited edition pressing of Elephant features a stunning red vinyl with gatefold sleeve and exclusive liner notes. The album includes all the classic tracks that defined a generation of rock music.',
      rating: 5,
      reviews: 128,
      inStock: true,
      featured: true,
      sku: 'VIN-ELEPHANT-001',
      weight: '180g',
      dimensions: '12" x 12"',
      tracklist: [
        'Seven Nation Army',
        'Black Math',
        'There\'s No Home for You Here',
        'I Just Don\'t Know What to Do with Myself',
        'In the Cold, Cold Night',
        'I Want to Be the Boy to Warm Your Mother\'s Heart',
        'You\'ve Got Her in Your Pocket',
        'Ball and Biscuit',
        'The Hardest Button to Button',
        'Little Acorns',
        'Hypnotize',
        'The Air Near My Fingers',
        'Girl, You Have No Faith in Medicine',
        'Well It\'s True That We Love One Another'
      ],
      features: [
        'Limited edition red vinyl',
        'Gatefold sleeve',
        'Exclusive liner notes',
        '180g heavyweight vinyl',
        'Remastered audio'
      ]
    },
    {
      id: 2,
      name: 'White Blood Cells Vinyl LP',
      category: 'vinyl',
      price: 24.99,
      originalPrice: null,
      image: storeVinyl,
      description: 'Classic white vinyl pressing',
      longDescription: 'The breakthrough album that launched The Kults into international stardom. This white vinyl pressing captures the raw energy and minimalist aesthetic that defined the band\'s sound.',
      rating: 5,
      reviews: 95,
      inStock: true,
      featured: false,
      sku: 'VIN-WBC-001',
      weight: '180g',
      dimensions: '12" x 12"',
      tracklist: [
        'Dead Leaves and the Dirty Ground',
        'Hotel Yorba',
        'I\'m Finding It Harder to Be a Gentleman',
        'Fell in Love with a Girl',
        'Expecting',
        'Little Room',
        'The Union Forever',
        'The Same Boy You\'ve Always Known',
        'We\'re Going to Be Friends',
        'Offend in Every Way',
        'I Think I Smell a Rat',
        'Aluminum',
        'Apple Blossom',
        'I Can\'t Wait',
        'Now Mary',
        'I Can Learn',
        'This Protector'
      ],
      features: [
        'White vinyl pressing',
        'Original artwork',
        '180g heavyweight vinyl',
        'Remastered audio'
      ]
    },
    {
      id: 3,
      name: 'The Kults T-Shirt',
      category: 'clothing',
      price: 24.99,
      originalPrice: null,
      image: storeShirt,
      description: 'Classic band logo t-shirt in black',
      longDescription: 'The iconic Kults logo t-shirt, featuring the band\'s signature design on premium cotton. Perfect for everyday wear or showing your support at concerts.',
      rating: 4,
      reviews: 67,
      inStock: true,
      featured: false,
      sku: 'TSH-KULTS-001',
      weight: '150g',
      dimensions: 'Fits true to size',
      sizes: ['XS', 'S', 'M', 'L', 'XL', 'XXL'],
      features: [
        '100% premium cotton',
        'Screen printed logo',
        'True to size fit',
        'Machine washable',
        'Made in USA'
      ]
    },
    {
      id: 4,
      name: 'Seven Nation Army Hoodie',
      category: 'clothing',
      price: 49.99,
      originalPrice: 59.99,
      image: storeShirt,
      description: 'Comfortable hoodie featuring the iconic song title',
      longDescription: 'Premium cotton blend hoodie featuring the iconic "Seven Nation Army" design. Perfect for cooler weather and casual wear.',
      rating: 5,
      reviews: 42,
      inStock: true,
      featured: true,
      sku: 'HOOD-SNA-001',
      weight: '400g',
      dimensions: 'Fits true to size',
      sizes: ['S', 'M', 'L', 'XL', 'XXL'],
      features: [
        '80% cotton, 20% polyester',
        'Embroidered design',
        'Kangaroo pocket',
        'Drawstring hood',
        'Machine washable'
      ]
    },
    {
      id: 5,
      name: 'Guitar Pick Set',
      category: 'accessories',
      price: 9.99,
      originalPrice: null,
      image: storeGuitarPick,
      description: 'Set of 5 custom White Stripes guitar picks',
      longDescription: 'Premium guitar picks featuring The Kults logo and design. Perfect for guitarists who want to play with style. Each pick is crafted from high-quality celluloid for excellent tone and durability.',
      rating: 4,
      reviews: 23,
      inStock: true,
      featured: false,
      sku: 'PICK-KULTS-001',
      weight: '15g',
      dimensions: 'Standard guitar pick size',
      features: [
        'Set of 5 picks',
        'High-quality celluloid',
        'Custom Kults design',
        'Standard thickness (0.73mm)',
        'Great for electric and acoustic'
      ]
    },
    {
      id: 6,
      name: 'Limited Edition Poster',
      category: 'collectibles',
      price: 19.99,
      originalPrice: null,
      image: storePoster,
      description: 'Exclusive concert poster from 2003 tour',
      longDescription: 'Rare concert poster from The Kults\' legendary 2003 tour. This limited edition print captures the energy and excitement of their live performances during this iconic period.',
      rating: 5,
      reviews: 31,
      inStock: false,
      featured: false,
      sku: 'POST-2003-001',
      weight: '50g',
      dimensions: '18" x 24"',
      features: [
        'Limited edition print',
        'High-quality paper',
        'Concert artwork',
        'Suitable for framing',
        'Collector\'s item'
      ]
    },
    {
      id: 7,
      name: 'Get Behind Me Satan Vinyl LP',
      category: 'vinyl',
      price: 27.99,
      originalPrice: null,
      image: storeVinyl,
      description: 'Experimental masterpiece on vinyl',
      longDescription: 'The experimental masterpiece that showcased The Kults\' musical evolution. This vinyl pressing captures the raw energy and innovative sound that made this album a critical favorite.',
      rating: 4,
      reviews: 78,
      inStock: true,
      featured: false,
      sku: 'VIN-GBMS-001',
      weight: '180g',
      dimensions: '12" x 12"',
      tracklist: [
        'Blue Orchid',
        'The Nurse',
        'My Doorbell',
        'Forever for Her (Is Over for Me)',
        'Little Ghost',
        'The Denial Twist',
        'White Moon',
        'Instinct Blues',
        'Passive Manipulation',
        'Take, Take, Take',
        'As Ugly as I Seem',
        'Red Rain',
        'I\'m Lonely (But I Ain\'t That Lonely Yet)'
      ],
      features: [
        '180g heavyweight vinyl',
        'Original artwork',
        'Remastered audio',
        'Gatefold sleeve',
        'Limited pressing'
      ]
    },
    {
      id: 8,
      name: 'Band Logo Cap',
      category: 'accessories',
      price: 19.99,
      originalPrice: null,
      image: storeCap,
      description: 'Embroidered band logo baseball cap',
      longDescription: 'Classic baseball cap featuring the embroidered Kults logo. Made from premium materials for comfort and durability. Perfect for casual wear or showing your support.',
      rating: 4,
      reviews: 15,
      inStock: true,
      featured: false,
      sku: 'CAP-KULTS-001',
      weight: '120g',
      dimensions: 'One size fits most',
      features: [
        '100% cotton twill',
        'Embroidered logo',
        'Adjustable strap',
        'One size fits most',
        'Machine washable'
      ]
    }
  ];

  const product = products.find(p => p.id === parseInt(id || '1'));
  
  // Set page title at the top level - use a fallback if product not found
  usePageTitle(product ? `${product.name} - The Kults Store` : 'Product Not Found - The Kults Store');
  
  // Auto-scroll to top when component mounts or product changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);
  
  if (!product) {
    return (
      <div className="product-details-page">
        <div className="container">
          <div className="error-message">
            <h2>Product Not Found</h2>
            <p>The product you're looking for doesn't exist.</p>
            <button className="btn btn-primary" onClick={() => navigate('/store')}>
              Back to Store
            </button>
          </div>
        </div>
      </div>
    );
  }

  const handleAddToCart = () => {
    // In real app, this would add to cart state/context
    console.log('Added to cart:', { product, quantity: selectedQuantity, size: selectedSize });
    navigate('/checkout');
  };

  const handleWishlist = () => {
    setIsWishlisted(!isWishlisted);
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: product.name,
        text: product.description,
        url: window.location.href
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };

  return (
    <div className="product-details-page">
      <section className="product-hero">
        <div className="container">
          <button className="back-btn" onClick={() => navigate('/store')}>
            <ArrowLeft size={20} />
            Back to Store
          </button>
        </div>
      </section>

      <section className="product-content">
        <div className="container">
          <div className="product-layout">
            {/* Product Images */}
            <div className="product-images">
              <div className="main-image">
                <img src={product.image} alt={product.name} />
                {product.featured && (
                  <div className="featured-badge">Featured</div>
                )}
                {!product.inStock && (
                  <div className="out-of-stock">Out of Stock</div>
                )}
              </div>
            </div>

            {/* Product Info */}
            <div className="product-info">
              <div className="product-header">
                <h1 className="product-title">{product.name}</h1>
                <div className="product-meta">
                  <span className="sku">SKU: {product.sku}</span>
                  <div className="product-actions">
                    <button 
                      className={`wishlist-btn ${isWishlisted ? 'active' : ''}`}
                      onClick={handleWishlist}
                      aria-label="Add to wishlist"
                    >
                      <Heart size={20} />
                    </button>
                    <button 
                      className="share-btn"
                      onClick={handleShare}
                      aria-label="Share product"
                    >
                      <Share2 size={20} />
                    </button>
                  </div>
                </div>
              </div>

              <div className="product-rating">
                <div className="stars">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      size={16}
                      className={i < product.rating ? 'filled' : 'empty'}
                    />
                  ))}
                </div>
                <span className="review-count">({product.reviews} reviews)</span>
              </div>

              <div className="product-price">
                <span className="current-price">${product.price}</span>
                {product.originalPrice && (
                  <span className="original-price">${product.originalPrice}</span>
                )}
                {product.originalPrice && (
                  <span className="discount">
                    Save ${(product.originalPrice - product.price).toFixed(2)}
                  </span>
                )}
              </div>

              <div className="product-description">
                <p>{product.longDescription}</p>
              </div>

              {/* Size Selection for Clothing */}
              {product.sizes && (
                <div className="size-selection">
                  <h3>Select Size</h3>
                  <div className="size-options">
                    {product.sizes.map((size) => (
                      <button
                        key={size}
                        className={`size-btn ${selectedSize === size ? 'selected' : ''}`}
                        onClick={() => setSelectedSize(size)}
                      >
                        {size}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Quantity Selection */}
              <div className="quantity-selection">
                <h3>Quantity</h3>
                <div className="quantity-controls">
                  <button 
                    className="qty-btn"
                    onClick={() => setSelectedQuantity(Math.max(1, selectedQuantity - 1))}
                    disabled={selectedQuantity <= 1}
                  >
                    -
                  </button>
                  <span className="quantity">{selectedQuantity}</span>
                  <button 
                    className="qty-btn"
                    onClick={() => setSelectedQuantity(selectedQuantity + 1)}
                  >
                    +
                  </button>
                </div>
              </div>

              {/* Add to Cart Button */}
              <button
                className="add-to-cart-btn"
                onClick={handleAddToCart}
                disabled={!product.inStock || (product.sizes && !selectedSize)}
              >
                <ShoppingCart size={20} />
                {!product.inStock 
                  ? 'Out of Stock' 
                  : product.sizes && !selectedSize 
                    ? 'Select Size' 
                    : 'Add to Cart'
                }
              </button>

              {/* Product Features */}
              <div className="product-features">
                <h3>Features</h3>
                <ul>
                  {product.features.map((feature, index) => (
                    <li key={index}>{feature}</li>
                  ))}
                </ul>
              </div>

              {/* Shipping Info */}
              <div className="shipping-info">
                <div className="info-item">
                  <Truck size={20} />
                  <div>
                    <h4>Free Shipping</h4>
                    <p>On orders over $50</p>
                  </div>
                </div>
                <div className="info-item">
                  <Shield size={20} />
                  <div>
                    <h4>Secure Payment</h4>
                    <p>SSL encrypted checkout</p>
                  </div>
                </div>
                <div className="info-item">
                  <RotateCcw size={20} />
                  <div>
                    <h4>Easy Returns</h4>
                    <p>30-day return policy</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Product Details Tabs */}
          <div className="product-tabs">
            <div className="tab-content">
              {product.tracklist && (
                <div className="tab-section">
                  <h3>Track Listing</h3>
                  <ol className="tracklist">
                    {product.tracklist.map((track, index) => (
                      <li key={index}>{track}</li>
                    ))}
                  </ol>
                </div>
              )}

              <div className="tab-section">
                <h3>Product Details</h3>
                <div className="product-specs">
                  <div className="spec-item">
                    <span className="spec-label">Weight:</span>
                    <span className="spec-value">{product.weight}</span>
                  </div>
                  <div className="spec-item">
                    <span className="spec-label">Dimensions:</span>
                    <span className="spec-value">{product.dimensions}</span>
                  </div>
                  <div className="spec-item">
                    <span className="spec-label">Category:</span>
                    <span className="spec-value">{product.category}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProductDetails; 